import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type DialogType = 'alert' | 'confirm';

type DialogRequest = {
  id: number;
  type: DialogType;
  title: string;
  message: string;
  confirmText: string;
  autoCloseMs?: number;
  cancelText?: string;
  resolve: (value: boolean) => void;
};

type ConfirmOptions = {
  title?: string;
  confirmText?: string;
  cancelText?: string;
};

type AdminDialogApi = {
  showAlert: (message: string, title?: string, confirmText?: string, autoCloseMs?: number) => Promise<void>;
  showConfirm: (message: string, options?: ConfirmOptions) => Promise<boolean>;
};

const AdminDialogContext = createContext<AdminDialogApi | null>(null);

export function useAdminDialog(): AdminDialogApi {
  const ctx = useContext(AdminDialogContext);
  if (!ctx) throw new Error('useAdminDialog deve ser usado dentro de AdminDialogProvider');
  return ctx;
}

export function AdminDialogProvider({ children }: { children: React.ReactNode }) {
  const idRef = useRef(0);
  const queueRef = useRef<DialogRequest[]>([]);
  const [currentDialog, setCurrentDialog] = useState<DialogRequest | null>(null);
  const currentDialogRef = useRef<DialogRequest | null>(null);
  currentDialogRef.current = currentDialog;

  const enqueueDialog = useCallback((request: Omit<DialogRequest, 'id'>) => {
    const id = ++idRef.current;
    queueRef.current.push({ ...request, id });
    if (!currentDialogRef.current) {
      const next = queueRef.current.shift() ?? null;
      setCurrentDialog(next);
    }
  }, []);

  useEffect(() => {
    if (currentDialog) return;
    if (queueRef.current.length === 0) return;
    setCurrentDialog(queueRef.current.shift() ?? null);
  }, [currentDialog]);

  const showAlert = useCallback((message: string, title = 'Aviso', confirmText = 'OK', autoCloseMs?: number) => {
    return new Promise<void>((resolve) => {
      enqueueDialog({
        type: 'alert',
        title,
        message,
        confirmText,
        autoCloseMs,
        resolve: () => resolve(),
      });
    });
  }, [enqueueDialog]);

  const showConfirm = useCallback((message: string, options?: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      enqueueDialog({
        type: 'confirm',
        title: options?.title ?? 'Confirmação',
        message,
        confirmText: options?.confirmText ?? 'Confirmar',
        cancelText: options?.cancelText ?? 'Cancelar',
        resolve,
      });
    });
  }, [enqueueDialog]);

  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (message?: unknown) => {
      const text = typeof message === 'string' ? message : String(message ?? '');
      void showAlert(text);
    };
    return () => {
      window.alert = originalAlert;
    };
  }, [showAlert]);

  const closeDialog = useCallback((result: boolean) => {
    const dialog = currentDialogRef.current;
    if (!dialog) return;
    currentDialogRef.current = null; // Evita double-resolve em cliques rápidos
    dialog.resolve(result);
    setCurrentDialog(null);
  }, []);

  useEffect(() => {
    if (!currentDialog || currentDialog.type !== 'alert' || !currentDialog.autoCloseMs) return;
    const timer = window.setTimeout(() => {
      closeDialog(true);
    }, currentDialog.autoCloseMs);
    return () => window.clearTimeout(timer);
  }, [currentDialog, closeDialog]);

  const api = useMemo<AdminDialogApi>(() => ({ showAlert, showConfirm }), [showAlert, showConfirm]);

  const modalContent = currentDialog ? (
    <div
      className="fixed inset-0 bg-black/50 z-[400] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-dialog-title"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex gap-4 mb-5">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M3 12a9 9 0 1018 0 9 9 0 10-18 0z" />
            </svg>
          </div>
          <div>
            <h3 id="admin-dialog-title" className="text-lg font-semibold text-gray-900 mb-1">{currentDialog.title}</h3>
            <p className="text-gray-600 whitespace-pre-line">{currentDialog.message}</p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          {currentDialog.type === 'confirm' && (
            <button
              type="button"
              onClick={() => closeDialog(false)}
              className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              {currentDialog.cancelText}
            </button>
          )}
          <button
            type="button"
            onClick={() => closeDialog(true)}
            className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 transition-colors"
          >
            {currentDialog.confirmText}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <AdminDialogContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' && document.body && createPortal(modalContent, document.body)}
    </AdminDialogContext.Provider>
  );
}
